class ImageSlider {
  constructor(element) {
    this.slider = element;
    this.track = this.slider.querySelector('.image-slider__track');
    this.slides = this.slider.querySelectorAll('.image-slider__slide');
    this.prevBtn = this.slider.querySelector('.image-slider__arrow--prev');
    this.nextBtn = this.slider.querySelector('.image-slider__arrow--next');
    this.indicators = this.slider.querySelectorAll('.image-slider__indicator');
    
    this.currentIndex = 0;
    this.isAutoplay = this.slider.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.slider.dataset.autoplaySpeed) * 1000 || 5000;
    this.autoplayTimer = null;
    this.isMobile = window.innerWidth <= 749;
    this.hasDesktopPeek = this.slider.dataset.desktopPeek === 'true';
    this.hasMobilePeek = this.slider.dataset.mobilePeek === 'true';
    
    this.init();
  }
  
  init() {
    if (this.slides.length <= 1) return;
    
    this.setupEventListeners();
    this.updateSlider();
    
    if (this.isAutoplay) {
      this.startAutoplay();
    }
    
    // Update mobile state on resize
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth <= 749;
      this.updateSlider();
    });
  }
  
  setupEventListeners() {
    // Navigation arrows
    this.prevBtn?.addEventListener('click', () => this.goToPrevious());
    this.nextBtn?.addEventListener('click', () => this.goToNext());
    
    // Indicators
    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => this.goToSlide(index));
    });
    
    // Keyboard navigation
    this.slider.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.goToPrevious();
      if (e.key === 'ArrowRight') this.goToNext();
    });
    
    // Touch/swipe support for mobile
    this.setupTouchEvents();
    
    // Pause autoplay on hover
    if (this.isAutoplay) {
      this.slider.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.slider.addEventListener('mouseleave', () => this.startAutoplay());
    }
  }
  
  goToSlide(index) {
    this.currentIndex = index;
    this.updateSlider();
    this.resetAutoplay();
  }
  
  goToNext() {
    this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    this.updateSlider();
    this.resetAutoplay();
  }
  
  goToPrevious() {
    this.currentIndex = this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1;
    this.updateSlider();
    this.resetAutoplay();
  }
  
  updateSlider() {
    // Calculate translation based on device and peek settings
    let translateX;
    if (this.isMobile) {
      translateX = this.hasMobilePeek ? -this.currentIndex * 94 : -this.currentIndex * 100;
    } else {
      translateX = this.hasDesktopPeek ? -this.currentIndex * 47 : -this.currentIndex * 50;
    }
    this.track.style.transform = `translateX(${translateX}%)`;
    
    // Update indicators
    this.indicators.forEach((indicator, index) => {
      indicator.classList.toggle('active', index === this.currentIndex);
    });
  }
  
  startAutoplay() {
    if (!this.isAutoplay) return;
    
    this.pauseAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.goToNext();
    }, this.autoplaySpeed);
  }
  
  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
  
  resetAutoplay() {
    if (this.isAutoplay) {
      this.startAutoplay();
    }
  }
  
  setupTouchEvents() {
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let isDragging = false;
    
    this.track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
      this.pauseAutoplay();
    });
    
    this.track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      
      currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = Math.abs(startX - currentX);
      const diffY = Math.abs(startY - currentY);
      
      // Only prevent default if horizontal swipe is more significant than vertical
      if (diffX > diffY && diffX > 10) {
        e.preventDefault();
      }
    });
    
    this.track.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;
      
      const diffX = startX - currentX;
      const threshold = 50;
      
      if (Math.abs(diffX) > threshold) {
        if (diffX > 0) {
          this.goToNext();
        } else {
          this.goToPrevious();
        }
      }
      
      this.resetAutoplay();
    });
  }
}

// Initialize all image sliders
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('.image-slider');
  sliders.forEach(slider => new ImageSlider(slider));
});

// Handle dynamic section loading (theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const slider = event.target.querySelector('.image-slider');
  if (slider) {
    new ImageSlider(slider);
  }
});
